import { Component, inject, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { ExcelUploadComponent } from '../../../../shared/excel-upload/excel-upload.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MbqService } from '../mbq.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject } from 'rxjs/internal/Subject';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../Common/pagination/pagination.component';
import { ConfirmDialogComponent } from '../../Common/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import {
  catchError,
  of,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  Subscription,
} from 'rxjs';
import { ProductsService } from '../../products/products.service';
import { InventoryService } from '../../inventory/inventory.service';

@Component({
  selector: 'app-upload-mbq',
  imports: [
    ExcelUploadComponent,
    NgSelectComponent,
    FormsModule,
    CommonModule,
    PaginationComponent,
    ConfirmDialogComponent,
    RouterLink,
  ],
  templateUrl: './upload-mbq.component.html',
  styleUrl: './upload-mbq.component.scss',
})
export class UploadMbqComponent {
  private toastr = inject(ToastrService);
  private activatedRoute = inject(ActivatedRoute);
  private mbqService = inject(MbqService);
  private productService = inject(ProductsService);
  private locationService = inject(InventoryService);
  private router = inject(Router);
  private confirmDialog = inject(ConfirmDialogService);
  title = 'MBQ Upload';
  uploadUrl: string = '';
  currentStoreId: string | null = null;
  private API_BASE_URL: string = `${environment.apiUrl}`;
  draftId: string | null = null;
  itemsList: any[] = [];
  productSearchResults: any = [];
  stores: any[] = [];
  selectedBaseProduct: any | null = null;
  public skuSearchSubject = new Subject<string>();
  selectedVariant: any | null = null;
  isSearchingSku = false;
  isSearchingName = false;
  productVariants: any[] = [];
  storeId: string | null = null;
  quantityToAdd: number = 0;
  searchText = '';
  page = 1; // current page (from API)
  limit = 10; // items per page
  total = 0; // total items count from API
  pageCount = 1;
  private productSearchSubscription!: Subscription;
  isViewMode = false;
  pagedData: any[] = [];
  newMbq: number = 0;
  newReorderLevel: number = 0;
  hasUnsavedChanges: boolean = false;
  public nameSearchSubject = new Subject<string>();
  @ViewChild('confirmDelete') confirmDelete!: ConfirmDialogComponent;

  ngOnInit(): void {
    this.getAllStores();
    this.setupProductSearch();
    this.storeId = this.getStoreIdFromLocalStorage();
    this.currentStoreId = this.getStoreIdFromLocalStorage();
    this.uploadUrl = `${this.API_BASE_URL}/mbq-draft/upload`;
    this.draftId = this.activatedRoute.snapshot.paramMap.get('id');
    this.activatedRoute.queryParams.subscribe((params) => {
      this.isViewMode = params['view'] === 'true';
      this.title = this.isViewMode
        ? 'View MBQ Draft'
        : this.draftId
          ? 'Update MBQ Draft'
          : 'MBQ Upload';
    });
    if (this.draftId) {
      this.getMbqListItems();
    }
  }

  getMbqListItems() {
    this.mbqService
      .getMbqDraftItems(this.draftId!, {
        limit: this.limit,
        page: this.page,
      })
      .subscribe((res: any) => {
        this.itemsList = res?.data ?? [];
        this.total = res?.total ?? this.itemsList.length;
        this.page = res?.page ?? 1;
        this.limit = res?.limit ?? this.limit;
        this.pageCount = res?.pageCount ?? 1;
      });
  }

  onPaginate(e: { page: number; limit: number }) {
    this.page = e.page;
    this.limit = e.limit;
    this.getMbqListItems();
  }

  private getStoreIdFromLocalStorage(): string | null {
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const userObject = JSON.parse(userString);
        // Accessing the 'storeId' nested within the 'user' object
        if (userObject && userObject.storeId) {
          return userObject.storeId;
        }
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
        this.toastr.error('Error reading user data.', 'System Error');
      }
    }
    return null;
  }

  onUploadSuccess(res: any): void {
    res = res.body;
    this.draftId = res?.draftId;
    this.itemsList = res?.data ?? [];
    this.total = res?.total ?? this.itemsList.length;
    this.page = res?.page ?? 1;
    this.limit = res?.limit ?? this.limit;
    this.pageCount = res?.pageCount ?? 1;
  }

  onUploadError(error: any): void {
    const errorMessage =
      error.error?.message ||
      error.message ||
      'Server error. Check the file content.';
    this.toastr.error(errorMessage, `Mbq Upload Failed`);
  }

  onProductSelected(result: any): void {
    this.selectedVariant = null;
    this.productVariants = [];
    this.selectedBaseProduct = null;
    this.quantityToAdd = 1;
    if (result) {
      this.selectedBaseProduct = result;
      const variants = this.selectedBaseProduct?.variants;
      if (variants && variants.length > 0) {
        this.productVariants = variants;
        if (this.productVariants.length === 1) {
          this.selectedVariant = this.productVariants[0];
        }
      } else {
        this.toastr.warning('The selected product has no variants.');
      }
    }
  }

  clearSearch(currentMode: 'sku' | 'name'): void {
    if (this.selectedBaseProduct) {
      return;
    }
    this.productSearchResults = [];
    if (currentMode === 'sku') {
      this.isSearchingSku = false;
    } else {
      this.isSearchingName = false;
    }
  }

  addProductToDraft(): void {
    const variantIdToAdd = this.selectedBaseProduct.variants[0].id;
    const itemToAdd: any = {
      sku: this.selectedBaseProduct.sku,
      variantId: variantIdToAdd, // The Variant's ID is the required variantId
      storeId: this.storeId,
      newMbq: this.newMbq,
      newReorderLevel: this.newReorderLevel,
    };
    this.mbqService
      .addDraftItem(this.draftId!, itemToAdd)
      .subscribe((res: any) => {
        this.newMbq = 0;
        this.newReorderLevel = 0;
        this.selectedBaseProduct = null;
        this.toastr.success('Item added successfully.', 'Success');
        this.getMbqListItems();
      });
  }

  onQtyChange(item: any): void {
    if (this.isViewMode) return;
    item.existingQty = Math.max(0, Number(item.existingQty) || 0);
    item.newQty = Math.max(0, Number(item.newQty) || 0);
    item.varianceQty = item.newQty - item.existingQty;

    // 🟢 CHANGE: Set flag true because the item quantity has been locally changed
    this.hasUnsavedChanges = true;
  }

  async deleteRow(item: any) {
    const ok = await this.confirmDelete.open({
      message: `Are you sure you want to delete ${item?.name ? '"' + item.name + '"' : 'this item'}?`,
    });
    if (!ok) return;

    this.mbqService
      .deletMbqItem(this.draftId!, item.id)
      .subscribe((res: any) => {
        this.toastr.success('Item deleted successfully.', 'Success');
        this.getMbqListItems();
      });
  }

  updateItem(item: any) {
    const body = {
      newMbq: item.newMbq,
      newReorderLevel: item.newReorderLevel,
    };
    this.mbqService
      .updateMbqDraftItem(this.draftId!, item.id, body)
      .subscribe((res: any) => {
        this.toastr.success('Item updated successfully.', 'Success');
      });
  }

  setupProductSearch(): void {
    // Helper function to handle the actual API call logic
    const handleSearch = (term: string, mode: 'sku' | 'query') => {
      if (term?.length > 2 && this.storeId) {
        // Set the correct loading flag
        if (mode === 'sku') {
          this.isSearchingSku = true;
          this.isSearchingName = false;
        } else {
          this.isSearchingSku = false;
          this.isSearchingName = true;
        }

        return this.productService
          .searchProducts(term, mode, this.storeId)
          .pipe(
            catchError(() => {
              this.toastr.error(`Failed to search products by ${mode}.`);
              return of({ data: [], total: 0 });
            }),
          );
      }

      this.isSearchingSku = false;
      this.isSearchingName = false;
      return of({ data: [], total: 0 });
    };

    // Subscription for SKU Search
    const skuSub = this.skuSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => handleSearch(term, 'sku')), // Use 'sku' mode
      )
      .subscribe((response: { data: any; total: number }) => {
        this.productSearchResults = response.data || [];
        this.isSearchingSku = false;
      });

    // Subscription for Product Name Search
    const nameSub = this.nameSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => handleSearch(term, 'query')), // Use 'query' (name) mode
      )
      .subscribe((response: { data: any; total: number }) => {
        this.productSearchResults = response.data || [];
        this.isSearchingName = false;
      });

    // Combine subscriptions for clean teardown
    this.productSearchSubscription = new Subscription();
    this.productSearchSubscription.add(skuSub);
    this.productSearchSubscription.add(nameSub);
  }

  async submitDraftItems() {
    const proceed = await this.confirmDialog.confirm({
      title: 'Submit MBQ?',
      message: `Are you sure you want to submit this MBQ draft (<strong>${this.itemsList.length}</strong> item(s))? The MBQ and reorder levels will be applied to the selected stores.`,
      confirmText: 'Submit',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    this.mbqService.submitMbqDraft(this.draftId!).subscribe((res: any) => {
      this.router.navigate(['/mbq']);
      this.getMbqListItems();
    });
  }

  save() {
    this.mbqService.saveMbqData(this.draftId!).subscribe((res: any) => {
      this.toastr.success('Data submitted successfully.', 'Success');
      this.getMbqListItems();
    });
  }

  getAllStores() {
    this.locationService.getAllStores().subscribe(
      (data: any) => {
        this.stores = data || [];
      },
      (err) => {
        console.error('Error fetching locations', err);
        this.toastr.error('Failed to load locations');
      },
    );
  }
}
